import {
  CANCEL_REASON_UNSELECTED,
  getCancelReasonName,
  USER_TREATMENT_STATE,
} from '@/common/constants/auth';
import { UserTreatment } from '@/models/user-treatment';

/**
 * A cancel request is only relevant while the treatment is still waiting on
 * one. Once it has been carried out or reverted, the reason belongs to the
 * treatment history rather than to the screens where staff act.
 */
export function hasOpenCancelRequest(treatment?: UserTreatment | null): boolean {
  return treatment?.state === USER_TREATMENT_STATE.CANCEL_REQUESTED;
}

/**
 * Returns the active (non-reverted) cancel request for a treatment, or null
 * when there is none — either because the state is wrong, or because
 * latest_cancel_request points at a reverted row (deprecated re-file path).
 */
export function resolveOpenCancelRequest(treatment?: UserTreatment | null) {
  if (!hasOpenCancelRequest(treatment)) {
    return null;
  }
  const req = treatment?.latest_cancel_request ?? null;
  return req && !req.reverted_at ? req : null;
}

/**
 * The reason the patient gave, or 未選択 when the request predates this feature
 * or arrived through the deprecated route.
 *
 * @returns null when there is no open request to describe.
 */
export function resolveCancelRequestReason(
  treatment?: UserTreatment | null,
): string | null {
  if (!hasOpenCancelRequest(treatment)) {
    return null;
  }

  const cancelRequest = treatment?.latest_cancel_request;
  // A reverted request is part of the history, not the current open request.
  // This happens when a patient re-files via the deprecated route (no new row
  // is created), leaving latest_cancel_request pointing at the reverted row.
  if (!cancelRequest || cancelRequest.reverted_at) {
    return CANCEL_REASON_UNSELECTED;
  }

  // The label resolved by the API wins, so a reason added there does not need a
  // matching release here.
  return (
    cancelRequest.reason_label || getCancelReasonName(cancelRequest.reason_code)
  );
}

/** The free text, which only exists when the patient chose その他. */
export function resolveCancelRequestDetail(
  treatment?: UserTreatment | null,
): string | null {
  if (!hasOpenCancelRequest(treatment)) {
    return null;
  }

  const cancelRequest = treatment?.latest_cancel_request;
  if (!cancelRequest || cancelRequest.reverted_at) {
    return null;
  }

  return cancelRequest.reason_text ?? null;
}
